import {
  Paper,
  OutlinedInput,
  InputAdornment,
  IconButton,
  FormControl,
  OutlinedInputProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { HighlightOffRounded } from '@mui/icons-material';
import { ChangeEvent, useEffect, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';

const PREFIX = 'TextSearch';

const classes = {
  search: `${PREFIX}-search`,
  searchNum: `${PREFIX}-searchNum`,
  spacing: `${PREFIX}-spacing`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`& .${classes.search}`]: {
    width: '100%',
    ['& .MuiInputLabel-root']: {
      left: theme.spacing(3.75),
      top: theme.spacing(0.625),
      '&.MuiFormLabel-filled, &.Mui-focused': {
        left: theme.spacing(0.25),
        top: 0,
      },
    },
    ['&:has(label.MuiFormLabel-filled), &:has(label.Mui-focused)']: {
      ['& input']: {
        color: theme.palette.lightTextPrimary.main,
        opacity: 1,
        WebkitTextFillColor: 'inherit',
      },
    },
    ['& .MuiInputBase-root']: {
      ['& input']: {
        paddingLeft: theme.spacing(1),
        '&::placeholder': {
          color: theme.palette.lightTextSecondary.main,
          opacity: 1,
        },
        '&:focus::-webkit-input-placeholder, & -moz-placeholder:focus, & -ms-input-placeholder:focus':
          {
            color: theme.palette.lightActionDisabled45p.main,
            opacity: 1,
          },
      },
      ['& input, & input.Mui-disabled']: {
        WebkitTextFillColor: 'inherit',
      },
      ['& .MuiInputAdornment-positionStart']: {
        width: '24px',
        height: '24px',
        margin: 0,
        padding: theme.spacing(1.5),
        paddingRight: 0,
      },
      ['& .MuiInputAdornment-positionEnd']: {
        width: '24px',
        height: '24px',
        marginRight: theme.spacing(1),
      },
      ['&:has(input:placeholder-shown)']: {
        ['& .MuiInputAdornment-positionEnd']: {
          visibility: 'hidden',
        },
      },
    },
  },

  [`& .${classes.searchNum}`]: {
    width: '100%',
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  },

  [`& .${classes.spacing}`]: {
    marginTop: theme.spacing(2),
  },
}));

function SearchInput(props: OutlinedInputProps) {
  return (
    <FormControl className={props.className} variant="outlined">
      <OutlinedInput
        id={props.id}
        inputProps={{ 'data-testid': 'search-input-test' }}
        type={props.type}
        label={props.label}
        placeholder={props.label?.toString()}
        value={props.value}
        disabled={props.disabled}
        onChange={props.onChange}
        startAdornment={props.startAdornment}
        endAdornment={props.endAdornment}></OutlinedInput>
    </FormControl>
  );
}

interface TextSearchProps {
  id: string;
  icon?: React.ReactNode;
  label?: string;
  placeholder?: string;
  currentSearchFilter?: string;
  type?: 'text' | 'number';
  disabled?: boolean;
  verticalSpacing?: boolean;
  className?: string;
  displayClearButton?: boolean;
  onSearchChange: (val: string) => void;
}

export default function TextSearch({
  id,
  icon,
  label,
  placeholder,
  currentSearchFilter,
  type = 'text',
  disabled = false,
  verticalSpacing = false,
  className,
  displayClearButton = true,
  onSearchChange,
}: TextSearchProps) {
  const [value, setValue] = useState(currentSearchFilter);
  const debouncedValue = useDebounce(value, 500);

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setValue(evt.target.value);
  };

  const clearSearch = () => {
    setValue('');
    onSearchChange('');
  };

  useEffect(() => {
    onSearchChange(debouncedValue || '');
  }, [debouncedValue]);

  return (
    <StyledPaper elevation={0} className={className}>
      <SearchInput
        id={id}
        className={`${type === 'number' ? classes.searchNum : classes.search}
          ${verticalSpacing ? classes.spacing : ''}`}
        label={label}
        placeholder={placeholder}
        value={value}
        type={type}
        disabled={disabled}
        onChange={handleChange}
        startAdornment={
          icon && <InputAdornment position="start">{icon}</InputAdornment>
        }
        endAdornment={
          displayClearButton && (
            <InputAdornment position="end">
              <IconButton
                data-testid="clear-test"
                disabled={!value}
                onClick={clearSearch}
                size="small">
                <HighlightOffRounded />
              </IconButton>
            </InputAdornment>
          )
        }
      />
    </StyledPaper>
  );
}
